import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import api from '../api/api';

const OfflinePayment = ({ orderNumber, price, sellerId, sellerName, bankDetails, instructions, isCashOnDelivery = false, isEFT = false, currencySymbol = "$" }) => {
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState(null);
  const [showPaymentMethodModal, setShowPaymentMethodModal] = useState(false);
  const [selectedPaymentMethod, setSelectedPaymentMethod] = useState('');
  const navigate = useNavigate();

  const handleConfirmPaymentClick = () => {
    if (isCashOnDelivery) {
      // For cash on delivery, directly process with 'cash' method
      handlePaymentMethodSelection('cash');
    } else if (isEFT) {
      // For EFT, directly process with 'eft' method
      handlePaymentMethodSelection('eft');
    } else {
      // For general offline payment, show modal to select payment method
      setShowPaymentMethodModal(true);
    }
  };

  const handlePaymentMethodSelection = async (method = null) => {
    const paymentMethod = method || selectedPaymentMethod;
    if (!paymentMethod) {
      setError('Please select a payment method');
      return;
    }

    setIsLoading(true);
    setError(null);
    setShowPaymentMethodModal(false);

    try {
      // Create a pending payment record with selected payment method
      const { data } = await api.post(
        '/order/create-offline-payment',
        { 
          orderNumber,
          price,
          paymentMethod: paymentMethod,
          bankDetails
        },
        { withCredentials: true }
      );

      if (data.success) {
        // Navigate to confirmation page with payment reference using sellerName
        navigate(`/${sellerName}/order/confirm?payment_reference=${data.paymentReference}`);
      } else {
        throw new Error(data.message || 'Failed to create offline payment');
      }
    } catch (err) {
      setError(err.response?.data?.message || 'Failed to process offline payment');
      console.error('Offline payment error:', err);
    } finally {
      setIsLoading(false);
    }
  };

  const closeModal = () => {
    setShowPaymentMethodModal(false);
    setSelectedPaymentMethod('');
    setError(null);
  };

  return (
    <div className="bg-white p-6 rounded-lg shadow-sm">
      <h3 className="text-lg font-semibold text-gray-800 mb-4">
        {isCashOnDelivery ? 'Cash on Delivery' : (isEFT ? 'Bank Transfer (EFT)' : 'Offline Payment Details')}
      </h3>
      
      {/* Bank Details for EFT */}
      {isEFT && bankDetails && (
        <div className="mb-4 p-4 bg-blue-50 rounded-md border border-blue-200">
          <h4 className="font-medium text-blue-900 mb-2 flex items-center gap-2">
            <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M3 10h18M7 15h1m4 0h1m-7 4h12a3 3 0 003-3V8a3 3 0 00-3-3H6a3 3 0 00-3 3v8a3 3 0 003 3z" />
            </svg>
            Bank Account Details
          </h4>
          <div className="bg-white p-3 rounded border border-blue-100">
            <pre className="whitespace-pre-wrap text-sm text-gray-700 font-mono">{bankDetails}</pre>
          </div>
        </div>
      )}

      {/* Instructions */}
      {instructions && (
        <div className="mb-4 p-4 bg-yellow-50 rounded-md border border-yellow-200">
          <h4 className="font-medium text-yellow-900 mb-2 flex items-center gap-2">
            <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M13 16h-1v-4h-1m1-4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
            </svg>
            Payment Instructions
          </h4>
          <p className="text-sm text-yellow-800 whitespace-pre-wrap">{instructions}</p>
        </div>
      )}

      <div className="mb-4">
        <p className="text-gray-600">
          {isCashOnDelivery 
            ? 'You will pay with cash when your order is delivered. Click the button below to confirm your order.'
            : isEFT
            ? 'Please make your payment using the bank details above. After payment, click the button below to confirm.'
            : 'Please make your payment using the bank details above. After payment, click the button below to confirm.'
          }
        </p>
      </div>

      {error && (
        <div className="mb-4 p-3 bg-red-50 text-red-700 rounded-md text-sm">
          {error}
        </div>
      )}

      <button
        onClick={handleConfirmPaymentClick}
        disabled={isLoading}
        className={`w-full py-2 px-4 rounded-md text-white font-medium ${
          isLoading 
            ? 'bg-gray-400 cursor-not-allowed' 
            : 'bg-blue-600 hover:bg-blue-700'
        }`}
      >
        {isLoading ? 'Processing...' : (isCashOnDelivery ? 'Confirm Order' : 'Confirm Payment')}
      </button>

      {/* Payment Method Selection Modal */}
      {showPaymentMethodModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white rounded-lg p-6 max-w-md w-full mx-4">
            <h3 className="text-lg font-semibold text-gray-800 mb-4">Select Payment Method</h3>
            
            <div className="space-y-3 mb-6">
              <div
                className={`flex items-center p-3 border rounded-lg cursor-pointer ${
                  selectedPaymentMethod === 'cash' 
                    ? 'border-blue-500 bg-blue-50' 
                    : 'border-gray-300 hover:border-gray-400'
                }`}
                onClick={() => setSelectedPaymentMethod('cash')}
              >
                <input
                  type="radio"
                  name="paymentMethod"
                  value="cash"
                  checked={selectedPaymentMethod === 'cash'}
                  onChange={() => setSelectedPaymentMethod('cash')}
                  className="mr-3"
                />
                <div>
                  <div className="font-medium text-gray-900">Cash</div>
                  <div className="text-sm text-gray-500">Pay with cash on delivery</div>
                </div>
              </div>

              <div
                className={`flex items-center p-3 border rounded-lg cursor-pointer ${
                  selectedPaymentMethod === 'eft' 
                    ? 'border-blue-500 bg-blue-50' 
                    : 'border-gray-300 hover:border-gray-400'
                }`}
                onClick={() => setSelectedPaymentMethod('eft')}
              >
                <input
                  type="radio"
                  name="paymentMethod"
                  value="eft"
                  checked={selectedPaymentMethod === 'eft'}
                  onChange={() => setSelectedPaymentMethod('eft')}
                  className="mr-3"
                />
                <div>
                  <div className="font-medium text-gray-900">EFT (Electronic Funds Transfer)</div>
                  <div className="text-sm text-gray-500">Pay via bank transfer</div>
                </div>
              </div>
            </div>

            {error && (
              <div className="mb-4 p-3 bg-red-50 text-red-700 rounded-md text-sm">
                {error}
              </div>
            )}

            <div className="flex space-x-3">
              <button
                onClick={closeModal}
                className="flex-1 py-2 px-4 border border-gray-300 rounded-md text-gray-700 hover:bg-gray-50"
              >
                Cancel
              </button>
              <button
                onClick={() => handlePaymentMethodSelection()}
                disabled={!selectedPaymentMethod || isLoading}
                className={`flex-1 py-2 px-4 rounded-md text-white font-medium ${
                  !selectedPaymentMethod || isLoading
                    ? 'bg-gray-400 cursor-not-allowed'
                    : 'bg-blue-600 hover:bg-blue-700'
                }`}
              >
                {isLoading ? 'Processing...' : 'Confirm'}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default OfflinePayment; 